// URL scrubbing for every analytics vendor (security audit finding
// "order-uuid-capability-in-tracked-page-url").
//
// An order id (a UUID) is a bearer key: whoever holds it can open that order's
// page, with the buyer's name, address and items. It sits in page URLs
// (/da/orders/<id>, /da/account/orders/<id>, /da/shop/order-confirmed?orderId=..
// &session_id=..), and analytics tools record the page URL by default. So
// every URL that leaves the browser for an analytics vendor goes through
// redactUrl() first, and session replay (Clarity) is off on the order pages.

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi

/** Query params that carry an order id or a payment session. Dropped. */
export const SENSITIVE_PARAMS = ['orderId', 'session_id', 'checkout', 'payment'] as const

const ABSOLUTE_RE = /^[a-z][a-z0-9+.-]*:/i
const PLACEHOLDER_BASE = 'https://redact.invalid'

/**
 * Returns the URL with every UUID replaced by `:id` and the sensitive query
 * params removed. Other params (utm_*, lang) are kept. Absolute URLs stay
 * absolute; relative ones (Umami sends a path) stay relative. Anything that
 * cannot be parsed comes back empty, so a malformed URL never leaks raw.
 */
export function redactUrl(url: string | null | undefined): string {
  if (!url) return ''
  const absolute = ABSOLUTE_RE.test(url)
  let parsed: URL
  try {
    parsed = new URL(url, PLACEHOLDER_BASE)
  } catch {
    return ''
  }
  for (const name of SENSITIVE_PARAMS) parsed.searchParams.delete(name)
  const out = absolute
    ? parsed.toString()
    : `${parsed.pathname}${parsed.search}${parsed.hash}`
  return out.replace(UUID_RE, ':id')
}

// /orders/<id>, /account/orders/<id> and /shop/order-confirmed, with or
// without the /{lang} prefix and a trailing slash.
const SENSITIVE_ROUTE_RE =
  /^(?:\/[a-z]{2})?\/(?:orders\/[^/]+|account\/orders\/[^/]+|shop\/order-confirmed)\/?$/

/** True on the pages that show an order's personal data (no session replay). */
export function isSensitiveRoute(pathname: string): boolean {
  return SENSITIVE_ROUTE_RE.test(pathname)
}

// ---------------------------------------------------------------------------
// Event parameters (BUG-479). Every event track() sends goes through
// redactParams first, for GTM's dataLayer and for GA4 alike.

/**
 * Parameter names that carry an order id, a payment session or personal data.
 * Dropped whatever their value. transaction_id (the human order number) is
 * not on the list: it is the GA4 purchase key and holds no UUID.
 */
const SENSITIVE_KEY_RE =
  /^(?:order_?id|session_?id|checkout_?session(?:_?id)?|client_?secret|payment_?intent(?:_?id)?|e_?mail|customer_?\w*|phone|tel|mobile|(?:first|last|full|user|customer)_?name|name|address\w*|line[12]|postal_?code|zip(?:code)?|city)$/i

const EMAIL_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/
/** GA4 drops event parameter values over 100 characters. */
const MAX_VALUE_LENGTH = 100

/** True for a parameter name that must never leave the browser. */
export function isSensitiveKey(key: string): boolean {
  return SENSITIVE_KEY_RE.test(key)
}

/**
 * A string value made safe to send: every UUID becomes `:id`, and a value
 * that holds an e-mail address is dropped (undefined). Capped at 100 chars.
 */
export function redactValue(value: string): string | undefined {
  if (EMAIL_RE.test(value)) return undefined
  return value.replace(UUID_RE, ':id').slice(0, MAX_VALUE_LENGTH)
}

/**
 * The parameters with sensitive keys dropped, undefined values dropped and
 * every string passed through redactValue. Arrays of objects (GA4 `items`)
 * are redacted the same way, one level deep. Numbers and booleans pass;
 * anything else (nested objects, functions, null) is dropped.
 */
export function redactParams(params: Readonly<Record<string, unknown>>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(params)) {
    if (isSensitiveKey(key)) continue
    if (typeof value === 'string') {
      const safe = redactValue(value)
      if (safe !== undefined) out[key] = safe
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      out[key] = value
    } else if (Array.isArray(value)) {
      out[key] = value
        .filter((entry): entry is Record<string, unknown> => typeof entry === 'object' && entry !== null)
        .map((entry) => redactParams(entry))
    }
  }
  return out
}
